import { Close } from '@mui/icons-material';
import {
  ClickAwayListener,
  Grow,
  IconButton,
  MenuList,
  Paper,
  PopoverProps,
  Popper,
  PopperProps,
  Stack,
  Typography,
} from '@mui/material';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { getColor } from '../assets/theme';

/**
 * Interface for the Popover component props
 * @extends Omit<PopoverProps, 'open'>
 */
export interface PopoverInterface extends Omit<PopoverProps, 'open'> {
  /** Content to be rendered inside the popover */
  children: ReactNode;
  /** If true, disables the ability to close the popover by clicking outside */
  disableOnClose?: boolean;
  /** If true, shows a header in the popover */
  showHeader?: boolean;
  /** Title to be displayed in the header */
  headerTitle?: string;
  /** Position of the popover relative to its anchor element */
  placement?:
    | 'top'
    | 'right'
    | 'bottom'
    | 'left'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'right-start'
    | 'right-end'
    | 'left-start'
    | 'left-end';
  disablePortal?: PopperProps['disablePortal'];
}

/**
 * Custom hook for managing a popover component
 * @returns {Object} An object containing the PopoverWrapper component and utility functions
 */
export const usePopover = () => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const { white, primary } = getColor();

  /**
   * Toggles the open state of the popover
   */
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  /**
   * Closes the popover
   * @param {Event | React.SyntheticEvent} event - The event that triggered the close action
   */
  const handleClose = (event?: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event?.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  /**
   * Handles keyboard events for the popover
   * @param {React.KeyboardEvent} event - The keyboard event
   */
  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current === true && open === false && anchorRef.current) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  /**
   * Memoized PopoverWrapper component
   */
  const PopoverWrapper: FC<PopoverInterface> = useMemo(
    () =>
      ({
        children,
        disableOnClose = false,
        showHeader = false,
        headerTitle = '',
        ...props
      }) => {
        return (
          <ClickAwayListener
            onClickAway={disableOnClose ? () => {} : handleClose}
          >
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              placement='top'
              transition
              disablePortal
              style={{ zIndex: 9999 }}
              {...props}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper>
                    <>
                      {showHeader && (
                        <Stack
                          sx={{
                            alignItems: 'center',
                            justifyContent: headerTitle
                              ? 'space-between'
                              : 'flex-end',
                            bgcolor: primary,
                            borderTopRightRadius: '4px',
                            borderTopLeftRadius: '4px',
                          }}
                          direction='row'
                        >
                          {headerTitle && (
                            <Typography sx={{ pl: 2, color: white }}>
                              {headerTitle}
                            </Typography>
                          )}
                          <IconButton
                            disableRipple
                            sx={[
                              {
                                bgcolor: 'transparent!important',
                                '&.MuiButtonBase-root': {
                                  bgcolor: 'transparent!important',
                                },
                                ':hover': {
                                  bgcolor: 'transparent',
                                  color: primary,
                                },
                                padding: headerTitle ? '24px' : '12px',
                              },
                            ]}
                            onClick={() => handleClose()}
                          >
                            <Close sx={{ color: white }} />
                          </IconButton>
                        </Stack>
                      )}
                      <MenuList
                        autoFocusItem={open}
                        onKeyDown={
                          disableOnClose
                            ? undefined
                            : (event) => {
                                if (!disableOnClose) {
                                  handleListKeyDown(event);
                                }
                              }
                        }
                      >
                        {children}
                      </MenuList>
                    </>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </ClickAwayListener>
        );
      },
    [open, anchorRef],
  );

  return { PopoverWrapper, handleToggle, anchorRef, handleClose, open };
};
